import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import './AnalysisTestUI.css';
import './Analytics.css';
import './SurveyAnalytics.css';

const COMPLETION_BUCKETS = [0, 20, 40, 60, 80, 100];
const isNotMentioned = (v) => typeof v === 'string' && v.trim().toLowerCase() === 'not mentioned';
const formatQuestionLabel = (value) => {
  const str = String(value ?? '').trim();
  if (!str) return 'Answer';
  if (/^Q/i.test(str)) return `Q${str.replace(/^Q/i, '')}`;
  if (/^\d+$/.test(str)) return `Q${str}`;
  return str;
};
const escapeCsvField = (value) => {
  const str = value == null ? '' : String(value);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

const API_BASE = process.env.REACT_APP_AGENT_API_URL || '';
const HOSPITAL_ENDPOINT = '/api/analysis-hospital-generate';
const GENERAL_ENDPOINT = '/api/analysis-general-generate';
const SURVEY_ENDPOINT = '/api/analysis-survey-generate';
const HOSPITAL_TYPES = new Set(['hospital-scheduling', 'hospital-test-results']);
const SURVEY_TYPE = 'survey';
const EXCLUDED_TYPES = new Set(['none']);
const CHART_COLORS = ['#0ea5e9', '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

const normalizeScriptId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object' && typeof value.$oid === 'string') {
    return value.$oid.trim();
  }
  const text = String(value).trim();
  return text === '[object Object]' ? '' : text;
};

const isPhoneCall = (log) => {
  const callId = log.callSid || log.callId || '';
  return String(callId).startsWith('CA');
};

const buildPercent = (count, total) => {
  if (!total) return 0;
  return Math.round((count / total) * 1000) / 10;
};

const STEPS = [
  { id: 'script', label: 'Step 1', description: 'Choose script & analysis type' },
  { id: 'configure', label: 'Step 2', description: 'Prompt & questions' },
  { id: 'run', label: 'Step 3', description: 'Run and results' },
];

const CONFIGURABLE_MAIN_TYPES = new Set([SURVEY_TYPE, ...HOSPITAL_TYPES]);

const AnalysisTestUI = () => {
  const [activeSubTab, setActiveSubTab] = useState('script');

  const [logs, setLogs] = useState([]);
  const [allScripts, setAllScripts] = useState([]);
  const [configuredScripts, setConfiguredScripts] = useState([]);
  const [analysisTypes, setAnalysisTypes] = useState({
    main: { none: { id: 'none', label: 'None', configurable: false } },
    extra: { none: { id: 'none', label: 'None', configurable: false } },
  });
  const [loading, setLoading] = useState(true);
  const [scriptsLoading, setScriptsLoading] = useState(true);
  const [typesLoading, setTypesLoading] = useState(true);

  const [selectedScriptId, setSelectedScriptId] = useState('');
  const [setupType, setSetupType] = useState('none');
  const [setupExtraAnalysis, setSetupExtraAnalysis] = useState([]);
  const [setupSaveMessage, setSetupSaveMessage] = useState('');
  const [setupSavingScript, setSetupSavingScript] = useState(false);

  const [promptDraft, setPromptDraft] = useState('');
  const [questionsDraft, setQuestionsDraft] = useState('');
  const [loadingSetupData, setLoadingSetupData] = useState(false);
  const [setupDataSaveState, setSetupDataSaveState] = useState('idle');
  const [setupDataError, setSetupDataError] = useState('');
  const [generateQuestionsState, setGenerateQuestionsState] = useState('idle');

  const [runScriptId, setRunScriptId] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [runError, setRunError] = useState('');

  const [statsBreakdown, setStatsBreakdown] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);
  const [surveyStats, setSurveyStats] = useState(null);
  const [batchStats, setBatchStats] = useState(null);
  const [sentimentStats, setSentimentStats] = useState(null);
  const [completionStats, setCompletionStats] = useState(null);
  const [surveyResults, setSurveyResults] = useState({});
  const [analysisResults, setAnalysisResults] = useState({});
  const [activeResultTab, setActiveResultTab] = useState('stats');
  const [transcriptModal, setTranscriptModal] = useState({ open: false, title: '', transcript: '' });

  const selectedScript = useMemo(
    () => allScripts.find((s) => s.id === selectedScriptId) || configuredScripts.find((s) => s.id === selectedScriptId),
    [allScripts, configuredScripts, selectedScriptId]
  );
  const runScript = useMemo(
    () => configuredScripts.find((s) => s.id === runScriptId) || null,
    [configuredScripts, runScriptId]
  );
  const scriptType = runScript ? String(runScript.type || '').trim().toLowerCase() : '';
  const scriptName = runScript?.name || selectedScript?.name || '';

  useEffect(() => {
    if (activeSubTab === 'run' && selectedScriptId) setRunScriptId(selectedScriptId);
  }, [activeSubTab, selectedScriptId]);

  const callsForScript = useMemo(() => {
    if (!runScriptId) return [];
    return logs.filter((log) => {
      const logScriptId = normalizeScriptId(log.scriptId);
      if (logScriptId !== runScriptId) return false;
      if (scriptType === SURVEY_TYPE) return true;
      return isPhoneCall(log);
    });
  }, [logs, runScriptId, scriptType]);

  const mainTypeOptions = useMemo(() => {
    const main = analysisTypes?.main || {};
    return Object.entries(main).map(([id, o]) => ({
      id,
      label: o?.label || id,
      configurable: !!o?.configurable,
    }));
  }, [analysisTypes]);

  const extraTypeOptions = useMemo(() => {
    const extra = analysisTypes?.extra || {};
    return Object.entries(extra)
      .filter(([, o]) => o?.id && o.id !== 'none')
      .map(([id, o]) => ({ id, label: o?.label || id }));
  }, [analysisTypes]);

  const isConfigurableType = CONFIGURABLE_MAIN_TYPES.has(setupType);
  const isSurveyType = setupType === SURVEY_TYPE;
  const setupLoadEndpoint = isSurveyType
    ? '/api/analysis-opinion-ai-setup'
    : HOSPITAL_TYPES.has(setupType)
      ? '/api/analysis-hospital-ai-setup'
      : '';
  const setupSaveEndpoint = isSurveyType
    ? '/api/analysis-opinion-ai-setup/save'
    : HOSPITAL_TYPES.has(setupType)
      ? '/api/analysis-hospital-ai-setup/save'
      : '';

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    fetch(`${API_BASE}/api/agentlogs`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (isMounted && data.success) setLogs(Array.isArray(data.logs) ? data.logs : []);
      })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  }, [getAuthHeaders]);

  useEffect(() => {
    let isMounted = true;
    setTypesLoading(true);
    fetch(`${API_BASE}/api/analysis-type-settings`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted || !data?.success) return;
        const main = (data.mainAnalysisTypes && typeof data.mainAnalysisTypes === 'object')
          ? Object.fromEntries(
              Object.entries(data.mainAnalysisTypes).map(([k, v]) => [
                k,
                { id: k, label: v?.label || k, configurable: !!v?.settings?.configurable },
              ])
            )
          : { none: { id: 'none', label: 'None', configurable: false } };
        const extra = (data.extraAnalysisTypes && typeof data.extraAnalysisTypes === 'object')
          ? Object.fromEntries(
              Object.entries(data.extraAnalysisTypes).map(([k, v]) => [
                k,
                { id: k, label: v?.label || k, configurable: !!v?.settings?.configurable },
              ])
            )
          : { none: { id: 'none', label: 'None', configurable: false } };
        setAnalysisTypes((prev) => ({ ...prev, main, extra }));
      })
      .finally(() => { if (isMounted) setTypesLoading(false); });
    return () => { isMounted = false; };
  }, [getAuthHeaders]);

  useEffect(() => {
    let isMounted = true;
    const loadScripts = () => {
      setScriptsLoading(true);
      Promise.all([
        fetch(`${API_BASE}/api/scripts`, { headers: getAuthHeaders() }).then((r) => r.json()),
        fetch(`${API_BASE}/api/analysis-scripts`, { headers: getAuthHeaders() }).then((r) => r.json()),
      ])
        .then(([scriptsRes, configuredRes]) => {
          if (!isMounted) return;
          const scriptsList = Array.isArray(scriptsRes?.scripts) ? scriptsRes.scripts : [];
          setAllScripts(
            scriptsList
              .map((s) => ({
                id: normalizeScriptId(s._id || s.id),
                name: String(s.scriptName || s.name || 'Untitled').trim() || 'Untitled',
              }))
              .filter((s) => s.id)
              .sort((a, b) => a.name.localeCompare(b.name))
          );
          const configuredList = Array.isArray(configuredRes?.scripts) ? configuredRes.scripts : [];
          const configured = configuredList
            .map((s) => ({
              id: normalizeScriptId(s?.scriptId || s?.id),
              name: String(s?.scriptName || s?.name || 'Untitled').trim() || 'Untitled',
              type: String(s?.settings?.type || '').trim().toLowerCase(),
            }))
            .filter((s) => s.id && !EXCLUDED_TYPES.has(s.type))
            .sort((a, b) => a.name.localeCompare(b.name));
          setConfiguredScripts(configured);
        })
        .finally(() => { if (isMounted) setScriptsLoading(false); });
    };
    loadScripts();
    return () => { isMounted = false; };
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!selectedScriptId || typesLoading) return;
    let isMounted = true;
    fetch(`${API_BASE}/api/analysis-scripts/get/${selectedScriptId}`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted || !data?.success) return;
        const settings = data?.script?.settings || {};
        const type = String(settings.type || 'none').trim().toLowerCase();
        setSetupType(mainTypeOptions.some((o) => o.id === type) ? type : 'none');
        const extra = Array.isArray(settings.extra_analysis) ? settings.extra_analysis : [];
        setSetupExtraAnalysis(extra.filter((t) => typeof t === 'string' && t.trim() && t !== 'none'));
      })
      .catch(() => {
        if (isMounted) {
          setSetupType('none');
          setSetupExtraAnalysis([]);
        }
      });
    return () => { isMounted = false; };
  }, [selectedScriptId, typesLoading, getAuthHeaders, mainTypeOptions]);

  const handleSaveSetup = async () => {
    if (!selectedScriptId) return;
    setSetupSavingScript(true);
    setSetupSaveMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/analysis-scripts/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          scriptId: selectedScriptId,
          settings: { type: setupType, extra_analysis: setupExtraAnalysis },
        }),
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || 'Save failed');
      setSetupSaveMessage('Saved.');
      setTimeout(() => setSetupSaveMessage(''), 2000);
      setConfiguredScripts((prev) => {
        const existing = prev.find((s) => s.id === selectedScriptId);
        const name = selectedScript?.name || existing?.name || 'Untitled';
        if (EXCLUDED_TYPES.has(setupType)) {
          return prev.filter((s) => s.id !== selectedScriptId);
        }
        if (existing) {
          return prev.map((s) => (s.id === selectedScriptId ? { ...s, type: setupType, name } : s));
        }
        return [...prev, { id: selectedScriptId, name, type: setupType }].sort((a, b) => a.name.localeCompare(b.name));
      });
    } catch (e) {
      setSetupSaveMessage(e?.message || 'Save failed');
    } finally {
      setSetupSavingScript(false);
    }
  };

  useEffect(() => {
    if (!selectedScriptId || !setupType || !setupLoadEndpoint) {
      setPromptDraft('');
      setQuestionsDraft('');
      return;
    }
    let isMounted = true;
    setLoadingSetupData(true);
    setSetupDataError('');
    fetch(`${API_BASE}${setupLoadEndpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ scriptId: selectedScriptId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted || !data?.success) return;
        setPromptDraft(String(data?.prompt || ''));
        setQuestionsDraft(isSurveyType ? String(data?.questions || '') : '');
      })
      .catch((err) => {
        if (isMounted) setSetupDataError(err?.message || 'Failed to load');
      })
      .finally(() => { if (isMounted) setLoadingSetupData(false); });
    return () => { isMounted = false; };
  }, [selectedScriptId, setupType, setupLoadEndpoint, isSurveyType, getAuthHeaders]);

  const handleSaveSetupData = async () => {
    if (!selectedScriptId || !setupSaveEndpoint) return;
    setSetupDataSaveState('saving');
    setSetupDataError('');
    try {
      const body = {
        scriptId: selectedScriptId,
        scriptName: selectedScript?.name,
        prompt: promptDraft,
        ...(isSurveyType ? { questions: questionsDraft } : {}),
      };
      const res = await fetch(`${API_BASE}${setupSaveEndpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || 'Save failed');
      setSetupDataSaveState('saved');
      setTimeout(() => setSetupDataSaveState('idle'), 2000);
    } catch (e) {
      setSetupDataError(e?.message || 'Save failed');
      setSetupDataSaveState('idle');
    }
  };

  const handleGenerateQuestions = async () => {
    if (!selectedScriptId || !isSurveyType) return;
    setGenerateQuestionsState('generating');
    setSetupDataError('');
    try {
      const res = await fetch(`${API_BASE}/api/analysis-opinion-ai-setup/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          scriptId: selectedScriptId,
          scriptName: selectedScript?.name,
          type: 'questions',
        }),
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || 'Generate failed');
      setQuestionsDraft(String(data?.questions || ''));
      setGenerateQuestionsState('generated');
      setTimeout(() => setGenerateQuestionsState('idle'), 2000);
    } catch (e) {
      setSetupDataError(e?.message || 'Generate failed');
      setGenerateQuestionsState('idle');
    }
  };

  const addExtraAnalysis = () => {
    const firstId = extraTypeOptions[0]?.id;
    if (firstId) setSetupExtraAnalysis((prev) => [...prev, firstId]);
  };

  const removeExtraAnalysis = (index) => {
    setSetupExtraAnalysis((prev) => prev.filter((_, i) => i !== index));
  };

  const setExtraAnalysisType = (index, value) => {
    setSetupExtraAnalysis((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const runAnalysis = async () => {
    if (!runScriptId || !callsForScript.length) {
      setRunError('Select a script and ensure there are calls to analyze.');
      return;
    }
    setRunError('');
    setStatsBreakdown(null);
    setSummaryStats(null);
    setSurveyStats(null);
    setBatchStats(null);
    setSentimentStats(null);
    setCompletionStats(null);
    setSurveyResults({});
    setAnalysisResults({});
    setRunning(true);
    setProgress({ processed: 0, total: callsForScript.length });
    const token = localStorage.getItem('token');
    const endpoint =
      scriptType === SURVEY_TYPE
        ? SURVEY_ENDPOINT
        : HOSPITAL_TYPES.has(scriptType)
          ? HOSPITAL_ENDPOINT
          : GENERAL_ENDPOINT;

    const sentimentTotals = { Positive: 0, Neutral: 0, Negative: 0, Unknown: 0 };
    const scheduledTotals = { Scheduled: 0, 'Not scheduled': 0 };
    const informedTotals = { 'Informed successfully': 0, 'Not informed': 0 };
    const extraQuestionsTotals = { 'Asked extra questions': 0, 'No extra questions': 0 };
    const scheduleRequestTotals = { 'Requested scheduling': 0, 'No scheduling request': 0 };
    const humanRequestTotals = { 'Requested a human': 0, 'Stayed with AI': 0 };
    const surveyAggregated = {};
    const surveySentimentCounts = { positive: 0, neutral: 0, negative: 0, unknown: 0 };
    const cache = {};

    try {
      for (let i = 0; i < callsForScript.length; i++) {
        const log = callsForScript[i];
        setProgress({ processed: i + 1, total: callsForScript.length });
        if (!log.transcript) continue;
        try {
          const body =
            scriptType === SURVEY_TYPE
              ? { transcript: log.transcript, logId: log._id, scriptId: runScriptId }
              : {
                  transcript: log.transcript,
                  logId: log._id,
                  scriptId: runScriptId,
                  analysisType: scriptType,
                };
          const res = await fetch(`${API_BASE}${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (!data?.success || !data?.analysis) continue;
          const a = data.analysis;
          cache[log._id] = a;
          const sentiment = String(a.sentiment || '').toLowerCase();

          if (scriptType === SURVEY_TYPE) {
            if (sentiment in surveySentimentCounts) surveySentimentCounts[sentiment] += 1;
            else surveySentimentCounts.unknown += 1;
            (a.questions || []).forEach((q) => {
              const qKey = q.questionNumber || q.question || 'Unknown';
              if (!surveyAggregated[qKey]) {
                surveyAggregated[qKey] = {
                  questionNumber: q.questionNumber,
                  question: q.question,
                  total: 0,
                  answers: {},
                };
              }
              surveyAggregated[qKey].total += 1;
              const answerKey = q.answer || `Option ${q.answerCode || 'N/A'}`;
              surveyAggregated[qKey].answers[answerKey] = (surveyAggregated[qKey].answers[answerKey] || 0) + 1;
            });
          } else {
            const extraQuestions = Boolean(a.extra_questions);
            const humanRequest = Boolean(a.requested_human);
            const scheduled = Boolean(a.scheduled_appointment);
            const informed = Boolean(a.informed_successfully ?? a.informed ?? a.scheduled_appointment);
            const scheduleRequest = Boolean(a.requested_scheduling ?? a.schedule_request ?? a.scheduled_appointment);
            scheduledTotals[scheduled ? 'Scheduled' : 'Not scheduled'] += 1;
            informedTotals[informed ? 'Informed successfully' : 'Not informed'] += 1;
            extraQuestionsTotals[extraQuestions ? 'Asked extra questions' : 'No extra questions'] += 1;
            scheduleRequestTotals[scheduleRequest ? 'Requested scheduling' : 'No scheduling request'] += 1;
            humanRequestTotals[humanRequest ? 'Requested a human' : 'Stayed with AI'] += 1;
            if (sentiment === 'positive') sentimentTotals.Positive += 1;
            else if (sentiment === 'neutral') sentimentTotals.Neutral += 1;
            else if (sentiment === 'negative') sentimentTotals.Negative += 1;
            else sentimentTotals.Unknown += 1;
          }
        } catch (_) {}
      }

      if (scriptType === SURVEY_TYPE) {
        setSurveyResults(cache);
        setBatchStats(surveyAggregated);
        setSentimentStats(surveySentimentCounts);
        setSurveyStats({ analyzed: Object.keys(cache).length, sentiment: surveySentimentCounts });
        const totalQuestionsCount = Object.keys(surveyAggregated).length
          ? Math.max(0, ...Object.values(surveyAggregated).map((s) => Number(String(s.questionNumber || '').replace(/^Q/i, '')) || 0))
          : 0;
        const completionBuckets = COMPLETION_BUCKETS.reduce((acc, p) => ({ ...acc, [p]: 0 }), {});
        callsForScript.forEach((log) => {
          const analysis = cache[log._id];
          if (!analysis) return;
          const uniqueQuestions = new Set(
            (analysis.questions || [])
              .filter((q) => !isNotMentioned(q.answer))
              .map((q) => q.questionNumber ?? q.question ?? q.answerCode ?? q.answer ?? 'Unknown')
          ).size;
          const answeredCount = totalQuestionsCount ? Math.min(uniqueQuestions, totalQuestionsCount) : uniqueQuestions;
          const percent = totalQuestionsCount ? Math.round((answeredCount / totalQuestionsCount) * 100) : 0;
          const bucket = COMPLETION_BUCKETS.reduce((prev, b) => (percent >= b ? b : prev), 0);
          completionBuckets[bucket] = (completionBuckets[bucket] || 0) + 1;
        });
        setCompletionStats(completionBuckets);
      } else {
        const totalCalls = Object.keys(cache).length;
        setAnalysisResults(cache);
        setStatsBreakdown(
          scriptType === 'hospital-scheduling'
            ? {
                scheduled: { title: 'Scheduled Appointment', answers: scheduledTotals },
                extraQuestions: { title: 'Extra Questions', answers: extraQuestionsTotals },
                humanRequest: { title: 'Requests for a Real Human', answers: humanRequestTotals },
                sentiment: { title: 'Sentiment Analysis', answers: sentimentTotals },
              }
            : {
                informed: { title: 'Successfully Informed', answers: informedTotals },
                extraQuestions: { title: 'Extra Questions', answers: extraQuestionsTotals },
                scheduleRequest: { title: 'Request to Schedule', answers: scheduleRequestTotals },
                humanRequest: { title: 'Requests for a Real Human', answers: humanRequestTotals },
                sentiment: { title: 'Sentiment Analysis', answers: sentimentTotals },
              }
        );
        setSummaryStats({
          totalCalls,
          primary: buildPercent(
            scriptType === 'hospital-scheduling' ? scheduledTotals.Scheduled : informedTotals['Informed successfully'],
            totalCalls
          ),
        });
      }
      } catch (err) {
      setRunError(err?.message || 'Analysis failed');
    } finally {
      setRunning(false);
      setProgress({ processed: 0, total: 0 });
    }
  };

  const isLoading = loading || scriptsLoading;
  const isScriptConfigured = runScriptId && configuredScripts.some((s) => s.id === runScriptId);
  const canRun = isScriptConfigured && callsForScript.length > 0 && !running;
  const hasResults = !!statsBreakdown || !!surveyStats || (batchStats && Object.keys(batchStats).length > 0);

  const questionOrder = useMemo(() => {
    if (!batchStats) return [];
    return Object.values(batchStats)
      .sort((a, b) => {
        const aNum = Number(String(a.questionNumber || '').replace(/^Q/i, ''));
        const bNum = Number(String(b.questionNumber || '').replace(/^Q/i, ''));
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
        return String(a.question || '').localeCompare(String(b.question || ''));
      })
      .map((qStat) => qStat.questionNumber || qStat.question);
  }, [batchStats]);

  return (
    <div className="analytics-page analysis-test-ui">
      <div className="analytics-header">
        <h1 className="analytics-title">Analysis Test UI</h1>
      </div>
      {isLoading && <div className="analysis-test-ui__loading">Loading…</div>}

      {!isLoading && (
        <>
          <div className="analysis-test-ui__top-bar">
            <nav className="analysis-test-ui__steps" aria-label="Process steps">
              {STEPS.map((step, index) => {
                const stepNumber = index + 1;
                const isActive = activeSubTab === step.id;
                const isPast = STEPS.findIndex((s) => s.id === activeSubTab) > index;
                return (
                  <React.Fragment key={step.id}>
                    <button
                      type="button"
                      className={`analysis-test-ui__step ${isActive ? 'analysis-test-ui__step--active' : ''} ${isPast ? 'analysis-test-ui__step--past' : ''}`}
                      onClick={() => setActiveSubTab(step.id)}
                      aria-current={isActive ? 'step' : undefined}
                    >
                      <span className="analysis-test-ui__step-number">{stepNumber}</span>
                      <span className="analysis-test-ui__step-label">{step.label}</span>
                      <span className="analysis-test-ui__step-desc">{step.description}</span>
                    </button>
                    {index < STEPS.length - 1 && (
                      <span className="analysis-test-ui__step-connector" aria-hidden="true" />
                    )}
                  </React.Fragment>
                );
              })}
            </nav>
            {(() => {
              const currentIndex = STEPS.findIndex((s) => s.id === activeSubTab);
              const hasPrev = currentIndex > 0;
              const hasNext = currentIndex >= 0 && currentIndex < STEPS.length - 1;
              return (
                <div className="analysis-test-ui__step-nav">
                  <button
                    type="button"
                    className="analysis-test-ui__btn analysis-test-ui__btn--ghost"
                    onClick={() => setActiveSubTab(STEPS[currentIndex - 1].id)}
                    disabled={!hasPrev}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="analysis-test-ui__btn analysis-test-ui__btn--primary"
                    onClick={() => setActiveSubTab(STEPS[currentIndex + 1].id)}
                    disabled={!hasNext}
                  >
                    Next
                  </button>
                </div>
              );
            })()}
          </div>

          <div className="analysis-test-ui__card">
            {activeSubTab === 'script' && (
              <div className="analysis-test-ui__panel">
                <h2 className="analysis-test-ui__panel-title">Choose script &amp; analysis type</h2>
                <p className="analysis-test-ui__panel-desc">
                  Select a script and assign its analysis type and extra analyses. Save to apply.
                </p>
                <div className="analysis-test-ui__row">
                  <label className="analysis-test-ui__label">Script</label>
                  <select
                    className="analysis-test-ui__select"
                    value={selectedScriptId}
                    onChange={(e) => setSelectedScriptId(e.target.value)}
                  >
                    <option value="">Select script</option>
                    {allScripts.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedScriptId && (
                  <>
                    <div className="analysis-test-ui__row">
                      <label className="analysis-test-ui__label">Analysis type</label>
                      <select
                        className="analysis-test-ui__select"
                        value={setupType}
                        onChange={(e) => setSetupType(e.target.value)}
                      >
                        {mainTypeOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="analysis-test-ui__row">
                      <label className="analysis-test-ui__label">Extra analysis</label>
                      <div className="analysis-test-ui__extra-row">
                        {setupExtraAnalysis.map((extraId, index) => (
                          <div key={`extra-${index}`} className="analysis-test-ui__extra-item">
                            <select
                              className="analysis-test-ui__select analysis-test-ui__select--sm"
                              value={extraId}
                              onChange={(e) => setExtraAnalysisType(index, e.target.value)}
                            >
                              {extraTypeOptions.map((o) => (
                                <option key={o.id} value={o.id}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="analysis-test-ui__btn analysis-test-ui__btn--remove"
                              onClick={() => removeExtraAnalysis(index)}
                              title="Remove"
                            >
                              −
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="analysis-test-ui__btn analysis-test-ui__btn--add"
                          onClick={addExtraAnalysis}
                          disabled={!extraTypeOptions.length}
                          title="Add extra analysis"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                    <div className="analysis-test-ui__row">
                      <button
                        type="button"
                        className="analysis-test-ui__btn analysis-test-ui__btn--primary"
                        onClick={handleSaveSetup}
                        disabled={setupSavingScript}
                      >
                        {setupSavingScript ? 'Saving…' : 'Save script settings'}
                      </button>
                      {setupSaveMessage && (
                        <span className={setupSaveMessage === 'Saved.' ? 'analysis-test-ui__ok' : 'analysis-test-ui__error-inline'}>
                          {setupSaveMessage}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeSubTab === 'configure' && (
              <div className="analysis-test-ui__panel">
                <h2 className="analysis-test-ui__panel-title">Prompt &amp; questions</h2>
                <p className="analysis-test-ui__panel-desc">
                  Customize the analysis prompt and, for survey scripts, the questions. Use &lt;questions&gt; in the prompt to inject survey questions.
                </p>
                {!selectedScriptId ? (
                  <p className="analysis-test-ui__hint">Go to Step 1 to choose a script and analysis type.</p>
                ) : !isConfigurableType ? (
                  <p className="analysis-test-ui__hint">This analysis type does not use a custom prompt. Choose Survey or a hospital type in Step 1 to customize prompt and questions.</p>
                ) : (
                  <>
                    <div className="analysis-test-ui__row">
                      <span className="analysis-test-ui__label">Script</span>
                      <span className="analysis-test-ui__script-readonly">{selectedScript?.name || selectedScriptId}</span>
                      <span className="analysis-test-ui__meta">({mainTypeOptions.find((o) => o.id === setupType)?.label || setupType})</span>
                    </div>
                    {setupDataError && (
                      <div className="analysis-test-ui__error-inline">{setupDataError}</div>
                    )}
                    <div className="analysis-test-ui__row">
                      <label className="analysis-test-ui__label">Prompt</label>
                      <textarea
                        className="analysis-test-ui__textarea"
                        value={loadingSetupData ? 'Loading…' : promptDraft}
                        onChange={(e) => setPromptDraft(e.target.value)}
                        disabled={loadingSetupData}
                        rows={8}
                        placeholder="Analysis prompt template…"
                      />
                    </div>
                    {isSurveyType && (
                      <div className="analysis-test-ui__row">
                        <label className="analysis-test-ui__label">Questions</label>
                        <div className="analysis-test-ui__questions-wrap">
                          <textarea
                            className="analysis-test-ui__textarea"
                            value={loadingSetupData ? 'Loading…' : questionsDraft}
                            onChange={(e) => setQuestionsDraft(e.target.value)}
                            disabled={loadingSetupData}
                            rows={10}
                            placeholder="One question per line or in a block…"
                          />
                          <button
                            type="button"
                            className="analysis-test-ui__btn analysis-test-ui__btn--secondary"
                            onClick={handleGenerateQuestions}
                            disabled={loadingSetupData || generateQuestionsState === 'generating'}
                          >
                            {generateQuestionsState === 'generating'
                              ? 'Generating…'
                              : generateQuestionsState === 'generated'
                                ? 'Generated'
                                : 'Generate from script'}
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="analysis-test-ui__row">
                      <button
                        type="button"
                        className="analysis-test-ui__btn analysis-test-ui__btn--primary"
                        onClick={handleSaveSetupData}
                        disabled={!setupSaveEndpoint || setupDataSaveState === 'saving'}
                      >
                        {setupDataSaveState === 'saving' ? 'Saving…' : setupDataSaveState === 'saved' ? 'Saved' : 'Save prompt & questions'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeSubTab === 'run' && (
              <div className="analysis-test-ui__panel">
                <h2 className="analysis-test-ui__panel-title">Run and results</h2>
                <p className="analysis-test-ui__panel-desc">
                  Run analysis on the script you chose and configured. Results appear below.
                </p>
                {selectedScriptId ? (
                  <>
                    <div className="analysis-test-ui__row">
                      <span className="analysis-test-ui__label">Script</span>
                      <span className="analysis-test-ui__script-readonly">{scriptName || runScriptId || selectedScriptId}</span>
                      {runScriptId && (
                        <span className="analysis-test-ui__meta">
                          {callsForScript.length} call{callsForScript.length !== 1 ? 's' : ''} to analyze
                        </span>
                      )}
                    </div>
                    {!configuredScripts.some((s) => s.id === selectedScriptId) && (
                      <p className="analysis-test-ui__hint">Configure this script in Step 2 first (set analysis type and save).</p>
                    )}
                    {running && progress.total > 0 && (
                      <div className="batch-progress" style={{ marginBottom: '1rem' }}>
                        <div className="batch-progress__label">
                          Processing {progress.processed} / {progress.total} calls
                        </div>
                        <div className="batch-progress__track">
                          <div
                            className="batch-progress__bar"
                            style={{
                              width: `${Math.min(100, progress.total ? Math.round((progress.processed / progress.total) * 100) : 0)}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="analysis-test-ui__actions">
                      <button
                        type="button"
                        className="analysis-test-ui__btn analysis-test-ui__btn--primary"
                        onClick={runAnalysis}
                        disabled={!canRun}
                      >
                        {running ? `Analyzing… ${progress.processed}/${progress.total}` : 'Run analysis'}
                      </button>
                    </div>
                    {runError && (
                      <div className="analysis-test-ui__error" role="alert">
                        {runError}
                      </div>
                    )}

                    {hasResults && (
                      <div className="analysis-test-ui__results-block batch-stats">
                        <div className="analysis-test-ui__result-tabs">
                          <button
                            type="button"
                            className={`analysis-test-ui__result-tab ${activeResultTab === 'stats' ? 'analysis-test-ui__result-tab--active' : ''}`}
                            onClick={() => setActiveResultTab('stats')}
                          >
                            Statistics
                          </button>
                          <button
                            type="button"
                            className={`analysis-test-ui__result-tab ${activeResultTab === 'table' ? 'analysis-test-ui__result-tab--active' : ''}`}
                            onClick={() => setActiveResultTab('table')}
                          >
                            Response table
                          </button>
                        </div>

                        {activeResultTab === 'stats' && (
                          <div className="batch-stats__content">
                            {scriptType === SURVEY_TYPE && batchStats && (
                              <>
                                {Object.values(batchStats)
                                  .sort((a, b) => (a.questionNumber || 0) - (b.questionNumber || 0))
                                  .map((qStat) => {
                                    const questionLabel = qStat.questionNumber
                                      ? `Q${String(qStat.questionNumber).replace(/^Q/i, '')}`
                                      : 'Question';
                                    return (
                                      <div key={qStat.questionNumber || qStat.question} className="question-stat">
                                        <div>
                                          <div className="question-meta">Question</div>
                                          <div className="question-title">{questionLabel}</div>
                                          <div className="question-text">{qStat.question}</div>
                                          <div className="question-meta">Responses: {qStat.total}</div>
                                        </div>
                                        <div className="answer-and-chart">
                                          <div className="answer-list">
                                            {Object.entries(qStat.answers)
                                              .sort((a, b) => b[1] - a[1])
                                              .map(([answer, count]) => {
                                                const percent = Math.round((count / qStat.total) * 1000) / 10;
                                                return (
                                                  <div key={answer} className="answer-row">
                                                    <span className="answer">{answer}</span>
                                                    <span className="percent">{percent}%</span>
                                                  </div>
                                                );
                                              })}
                                          </div>
                                          <div className="chart-wrapper">
                                            <ResponsiveContainer width="100%" height={240}>
                                              <PieChart margin={{ top: 4, right: 8, bottom: 28, left: 8 }}>
                                                <Pie
                                                  data={Object.entries(qStat.answers).map(([name, value]) => ({ name, value }))}
                                                  dataKey="value"
                                                  nameKey="name"
                                                  innerRadius="42%"
                                                  outerRadius="78%"
                                                  paddingAngle={1}
                                                >
                                                  {Object.entries(qStat.answers).map(([name], idx) => (
                                                    <Cell key={name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                                  ))}
                                                </Pie>
                                                <Tooltip formatter={(value) => `${value} responses`} />
                                                <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                                              </PieChart>
                                            </ResponsiveContainer>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                {sentimentStats && (
                                  <div className="sentiment-stat">
                                    <div>
                                      <div className="question-meta">Sentiment</div>
                                      <div className="question-title">Respondent attitude</div>
                                      <div className="question-text">Breakdown of sentiment across analyzed calls.</div>
                                    </div>
                                    <div className="answer-and-chart">
                                      <div className="answer-list">
                                        {Object.entries(sentimentStats)
                                          .filter(([, count]) => count > 0)
                                          .sort((a, b) => b[1] - a[1])
                                          .map(([sentiment, count]) => {
                                            const total = Object.values(sentimentStats).reduce((s, v) => s + v, 0);
                                            const percent = total ? Math.round((count / total) * 1000) / 10 : 0;
                                            return (
                                              <div key={sentiment} className="answer-row">
                                                <span className="answer">{sentiment}</span>
                                                <span className="percent">{percent}%</span>
                                              </div>
                                            );
                                          })}
                                      </div>
                                      <div className="chart-wrapper">
                                        <ResponsiveContainer width="100%" height={240}>
                                          <PieChart margin={{ top: 4, right: 8, bottom: 28, left: 8 }}>
                                            <Pie
                                              data={Object.entries(sentimentStats)
                                                .filter(([, value]) => value > 0)
                                                .map(([name, value]) => ({ name, value }))}
                                              dataKey="value"
                                              nameKey="name"
                                              innerRadius="42%"
                                              outerRadius="78%"
                                              paddingAngle={1}
                                            >
                                              {Object.entries(sentimentStats)
                                                .filter(([, value]) => value > 0)
                                                .map(([name], idx) => (
                                                  <Cell key={name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `${value} calls`} />
                                            <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                                          </PieChart>
                                        </ResponsiveContainer>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {completionStats && (
                                  <div className="completion-stat">
                                    <div>
                                      <div className="question-meta">Percent completion</div>
                                      <div className="question-title">Completion rate</div>
                                      <div className="question-text">Distribution by percent of questions completed.</div>
                                    </div>
                                    <div className="answer-and-chart">
                                      <div className="answer-list">
                                        {Object.entries(completionStats)
                                          .filter(([, count]) => count > 0)
                                          .sort((a, b) => Number(b[0]) - Number(a[0]))
                                          .map(([percent, count]) => (
                                            <div key={percent} className="answer-row">
                                              <span className="answer">{percent}%</span>
                                              <span className="percent">{count}</span>
                                            </div>
                                          ))}
                                      </div>
                                      <div className="chart-wrapper">
                                        <ResponsiveContainer width="100%" height={240}>
                                          <PieChart margin={{ top: 4, right: 8, bottom: 28, left: 8 }}>
                                            <Pie
                                              data={Object.entries(completionStats)
                                                .filter(([, value]) => value > 0)
                                                .map(([name, value]) => ({ name: `${name}%`, value }))}
                                              dataKey="value"
                                              nameKey="name"
                                              innerRadius="42%"
                                              outerRadius="78%"
                                              paddingAngle={1}
                                            >
                                              {Object.entries(completionStats)
                                                .filter(([, value]) => value > 0)
                                                .map(([name], idx) => (
                                                  <Cell key={name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `${value} respondents`} />
                                            <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                                          </PieChart>
                                        </ResponsiveContainer>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                            {scriptType !== SURVEY_TYPE && statsBreakdown && summaryStats && (
                              <div className="analysis-test-ui__result">
                                <p>
                                  Total calls analyzed: <strong>{summaryStats.totalCalls}</strong>
                                  {summaryStats.primary != null && ` · Primary rate: ${summaryStats.primary}%`}
                                </p>
                                <div className="analysis-test-ui__charts">
                                  {Object.entries(statsBreakdown).map(([key, stat]) => {
                                    const total = Object.values(stat.answers).reduce((s, n) => s + n, 0);
                                    if (!total) return null;
                                    return (
                                      <div key={key} className="analysis-test-ui__chart-block">
                                        <div className="analysis-test-ui__chart-title">{stat.title}</div>
                                        <ResponsiveContainer width="100%" height={220}>
                                          <PieChart margin={{ top: 4, right: 8, bottom: 28, left: 8 }}>
                                            <Pie
                                              data={Object.entries(stat.answers).map(([name, value]) => ({ name, value }))}
                                              dataKey="value"
                                              nameKey="name"
                                              innerRadius="42%"
                                              outerRadius="78%"
                                              paddingAngle={1}
                                            >
                                              {Object.entries(stat.answers).map(([name], idx) => (
                                                <Cell key={name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                              ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `${value} calls`} />
                                            <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 12 }} />
                                          </PieChart>
                                        </ResponsiveContainer>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {activeResultTab === 'table' && (
                          <div className="batch-stats__content">
                            {scriptType === SURVEY_TYPE && (
                              <div className="analysis-test-ui__table-wrap">
                                <table className="analysis-test-ui__table">
                                  <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Call ID</th>
                                      <th>Date</th>
                                      {questionOrder.map((qKey) => (
                                        <th key={qKey}>{formatQuestionLabel(qKey)} Answer</th>
                                      ))}
                                      <th>Sentiment</th>
                                      <th>Transcript</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {callsForScript
                                      .filter((log) => surveyResults[log._id])
                                      .map((log, idx) => {
                                        const analysis = surveyResults[log._id];
                                        if (!analysis) return null;
                                        const name = log.crmEntry?.contact?.fullName ||
                                          [log.crmEntry?.contact?.firstName, log.crmEntry?.contact?.lastName].filter(Boolean).join(' ') ||
                                          `Respondent ${idx + 1}`;
                                        const callId = log.callSid || log.callId || '';
                                        const date = new Date(log.createdAt).toLocaleDateString();
                                        const answerMap = {};
                                        (analysis.questions || []).forEach((q) => {
                                          const qKey = q.questionNumber || q.question || q.answerCode || q.answer || '';
                                          answerMap[qKey] = q.answer || 'Not mentioned';
                                        });
                                        return (
                                          <tr key={log._id}>
                                            <td>{name}</td>
                                            <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{callId.substring(0, 12)}…</td>
                                            <td>{date}</td>
                                            {questionOrder.map((qKey) => (
                                              <td key={qKey}>{answerMap[qKey] || 'Not mentioned'}</td>
                                            ))}
                                            <td>
                                              <span className={`analysis-test-ui__sentiment-badge analysis-test-ui__sentiment-badge--${(analysis.sentiment || '').toLowerCase()}`}>
                                                {analysis.sentiment || 'Unknown'}
                                              </span>
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="table-transcript-button"
                                                disabled={!log.transcript}
                                                onClick={() => setTranscriptModal({
                                                  open: true,
                                                  title: name,
                                                  transcript: log.transcript || 'No transcript available.',
                                                })}
                                              >
                                                Show
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                                {callsForScript.filter((log) => surveyResults[log._id]).length === 0 && (
                                  <p className="analysis-test-ui__hint">No responses with analysis data.</p>
                                )}
                              </div>
                            )}
                            {scriptType !== SURVEY_TYPE && (
                              <div className="analysis-test-ui__table-wrap">
                                <table className="analysis-test-ui__table">
                                  <thead>
                                    <tr>
                                      <th>Call ID</th>
                                      <th>Date</th>
                                      <th>Sentiment</th>
                                      <th>Transcript</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {callsForScript
                                      .filter((log) => analysisResults[log._id])
                                      .map((log) => {
                                        const a = analysisResults[log._id];
                                        const callId = log.callSid || log.callId || '';
                                        const date = new Date(log.createdAt).toLocaleDateString();
                                        return (
                                          <tr key={log._id}>
                                            <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{callId.substring(0, 12)}…</td>
                                            <td>{date}</td>
                                            <td>
                                              <span className={`analysis-test-ui__sentiment-badge analysis-test-ui__sentiment-badge--${(a.sentiment || '').toLowerCase()}`}>
                                                {a.sentiment || 'Unknown'}
                                              </span>
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="table-transcript-button"
                                                disabled={!log.transcript}
                                                onClick={() => setTranscriptModal({
                                                  open: true,
                                                  title: callId,
                                                  transcript: log.transcript || 'No transcript available.',
                                                })}
                                              >
                                                Show
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                                {callsForScript.filter((log) => analysisResults[log._id]).length === 0 && (
                                  <p className="analysis-test-ui__hint">No analysis data.</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="analysis-test-ui__hint">Go to Step 1 to choose a script.</p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {transcriptModal.open && (
        <div
          className="transcript-modal__backdrop"
          onClick={() => setTranscriptModal({ open: false, title: '', transcript: '' })}
        >
          <div className="transcript-modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="transcript-modal__header">
              <div>
                <div className="transcript-modal__title">Transcript</div>
                <div className="transcript-modal__subtitle">{transcriptModal.title}</div>
              </div>
              <button
                type="button"
                className="transcript-modal__close"
                onClick={() => setTranscriptModal({ open: false, title: '', transcript: '' })}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <pre className="transcript-modal__body" style={{ margin: 0, padding: '1rem 1.25rem', overflow: 'auto', flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {transcriptModal.transcript}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisTestUI;
